import { Component, Input, Output, EventEmitter, ElementRef, ViewChild, AfterViewInit, NgZone } from '@angular/core';
import { AppLogger, AppLoggerService } from '../../../core/logging';


@Component({
  selector: 'ui-modal',
  standalone: true,
  imports: [],
  templateUrl: './ui-modal.component.html',
  styleUrl: './ui-modal.component.scss'
})
export class UiModalComponent implements AfterViewInit {
  @Input() modalId: string = 'modal-' + Math.random().toString(36).substr(2, 9);
  @Input() title: string = '';
  @Input() size: 'sm' | 'md' | 'lg' | 'full' = 'md';
  @Input() showCloseButton: boolean = true;
  @Input() closeOnBackdrop: boolean = true;

  @Output() modalClosed = new EventEmitter<void>();

  @ViewChild('modalElement') modalElement!: ElementRef<HTMLDialogElement>;

  isOpen = false;
  private isClosing = false;

  private readonly log: AppLogger;

  constructor(private zone: NgZone, private appLogger: AppLoggerService) {
    this.log = this.appLogger.createForClass(UiModalComponent);
  }

  ngAfterViewInit() {
    // Listen for backdrop clicks
    if (this.closeOnBackdrop) {
      this.modalElement.nativeElement.addEventListener('click', (event) => {
        if (event.target === this.modalElement.nativeElement) {
          this.close();
        }
      });
    }
  }

  get modalBoxClasses(): string {
    const classes = ['modal-box'];

    if (this.size === 'sm') {
      classes.push('max-w-sm');
    } else if (this.size === 'lg') {
      // On mobile, make it fullscreen; on desktop use large width
      classes.push('max-w-5xl', 'lg:max-w-5xl', 'max-lg:max-w-full', 'max-lg:w-full', 'max-lg:h-full', 'max-lg:max-h-full', 'max-lg:m-0', 'max-lg:rounded-none');
    } else if (this.size === 'full') {
      classes.push('max-w-full', 'w-full', 'h-full', 'max-h-full', 'm-0', 'rounded-none');
    }
    // md is default in DaisyUI

    return classes.join(' ');
  }

  open() {
    this.isOpen = true;
    this.isClosing = false;

    const el = this.modalElement?.nativeElement;
    if (!el) return;

    // Defer to ensure the dialog is in DOM and after Angular has stabilized.
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        try {
          // Preferred: native dialog API.
          if (typeof el.showModal === 'function' && !el.open) {
            el.showModal();
          } else {
            el.setAttribute('open', '');
          }
        } catch {
          // Some browsers or cross-origin iframes can block showModal(); ensure it's visible.
          el.setAttribute('open', '');
        }
      }, 0);
    });
  }

  close() {
    // Prevent recursion - if already closing, don't emit again
    if (this.isClosing) {
      this.log.debug('ui-modal close() already in progress, skipping');
      return;
    }

    const el = this.modalElement?.nativeElement;

    this.log.debug('ui-modal close() called', { modalId: this.modalId });
    this.isClosing = true;
    this.isOpen = false;

    try {
      if (el?.open) {
        el.close();
      } else {
        el?.removeAttribute('open');
      }
    } catch {
      el?.removeAttribute('open');
    }

    this.log.debug('ui-modal emitting modalClosed event', { modalId: this.modalId });
    this.modalClosed.emit();

    // Reset the flag after a small delay to allow for future opens
    setTimeout(() => {
      this.isClosing = false;
    }, 100);
  }
}
